// The document a standard is saved as.
//
// `Standard.phases` is an untyped Array in Mongo, so this shape is not enforced
// anywhere — it is simply what the create-standard form has always written, and
// what a dozen consumers read: cableGeometry, drumCapacity, launchChecks and
// RebuildStandardDataFromJob on the front end, jobHelpers, jobUtils,
// productionProjection, factorySync, futureProducts, orders, phantoms and queues
// on the back. A field renamed here is a launch broken there, with nothing to
// catch it in between.
//
// So the wizard does not invent a shape. It fills these templates, and
// standardPayload.test.js checks the result against real standards exported from
// the live database.

import { disc, round4 } from './standardCascade.js';

// Number(null) is 0 and Number('') is 0, so absence has to be rejected before
// the conversion — otherwise a field nobody filled in is saved as a real zero,
// and "no drums specified" becomes "zero drums".
const num = v => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const str = v => {
  const s = String(v ?? '').trim();
  return s === '' ? null : s;
};
const upper = v => (str(v) ? str(v).toUpperCase() : null);

/** Phases in the order a cable is actually built. */
export const PHASE_ORDER = [
  'pre_stranding',
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'armouring',
  'exterior_sheat'
];

export const PHASE_LABEL = {
  pre_stranding: 'FUNIE CUPRU',
  conductor_isolation: 'IZOLARE',
  rope: 'INFUNIERE',
  interior_sheat: 'MANTA INTERIOARA',
  armouring: 'ARMARE',
  exterior_sheat: 'MANTA EXTERIOARA'
};

// What each phase is normally built on. Copied deliberately from the existing
// create-standard form rather than reasoned out afresh — `dependsOn` decides
// which tokens a phase consumes (jobHelpers) and which phase comes next
// (jobUtils, bobins), so a "better" answer here changes how cables are launched.
//
// `conductor_isolation` depends on `pre_stranding` — corrected 2026-10-17.
//
// It used to be null here, on the stated grounds that "one standard in the
// database (CYYF 5x16) does say pre_stranding while the rest say null". That was
// measured wrongly. Of the **89 standards that have a pre_stranding phase, 68
// say `pre_stranding` and 14 say null** — null is the minority, and it is the
// broken one:
//
//   factorySync.js builds a phase's `inputs` from the tokens of whatever phase
//   `dependsOn` names (`parentTokenIds`). With null there is no parent, so
//   conductor_isolation consumes nothing, and the copper rope the cable just
//   stranded for itself is never drawn down — it sits as available stock
//   forever. CYYF 5x35 was launched that way on 2026-10-12 and left 8 250 m of
//   phantom rope; CYYF 4x16, whose standard says `pre_stranding`, consumed its
//   16 480 m correctly.
//
// A cable that does NOT strand its own copper is unaffected: `resolveDependency`
// below only returns a natural parent that the cable actually has, and
// `pre_stranding` is first in PHASE_ORDER, so the walk-back finds nothing and
// conductor_isolation stays null — which is right for FY, MYF and every cable
// fed from bought-in wire.
const NATURAL_DEPENDENCY = {
  pre_stranding: null,
  conductor_isolation: 'pre_stranding',
  rope: 'conductor_isolation',
  interior_sheat: 'rope',
  armouring: 'interior_sheat',
  exterior_sheat: 'armouring'
};

/**
 * What a phase is built on top of, given the phases this cable actually has.
 *
 * A cable without armouring has its outer sheath sitting on the inner one, and
 * one without an inner sheath has it on the rope, so when the usual dependency
 * is absent the search walks back to whatever really is underneath.
 */
export function resolveDependency(phaseName, chosenNames) {
  const natural = NATURAL_DEPENDENCY[phaseName] ?? null;
  if (!natural) return null;
  if (chosenNames.includes(natural)) return natural;

  for (let i = PHASE_ORDER.indexOf(phaseName) - 1; i >= 0; i--) {
    if (chosenNames.includes(PHASE_ORDER[i])) return PHASE_ORDER[i];
  }
  return null;
}

export const emptyWire = () => ({
  material: 'COPPER',
  type: '',
  density: null,
  numberOfWires: [],
  wireDiameter: [],
  diameter: null,
  section: null
});

export const emptyIsolation = () => ({
  material: '',
  type: '',
  density: null,
  diameter: null,
  section: null,
  fillingSection: null,
  minRadial: null,
  maxRadial: null,
  color: ''
});

export const emptyConductor = () => ({ wire: emptyWire(), isolation: emptyIsolation() });

/** A blank phase carrying exactly the fields that phase is stored with. */
export function emptyPhase(name) {
  const base = { name, dependsOn: null };
  switch (name) {
    case 'pre_stranding':
      return {
        ...base,
        material: 'COPPER',
        type: '',
        density: null,
        wireDiameter: [],
        numberOfWires: [],
        numberOfDrums: null,
        diameter: null,
        section: null,
        rope: []
      };
    case 'conductor_isolation':
      return { ...base, conductors: [emptyConductor()] };
    case 'rope':
      return { ...base, diameter: null, section: null };
    case 'interior_sheat':
    case 'exterior_sheat':
      return {
        ...base,
        material: '',
        type: '',
        density: null,
        diameter: null,
        section: null,
        fillingSection: null,
        minRadial: null,
        maxRadial: null
      };
    case 'armouring':
      return {
        ...base,
        wide: null,
        thickness: null,
        density: null,
        diameter: null,
        section: null,
        fillingSection: null
      };
    default:
      return base;
  }
}

/** Prefill a wire or sheath block from a catalogue entry, leaving geometry alone. */
export function applyMaterialSpec(target, spec) {
  if (!spec) return target;
  return {
    ...target,
    material: spec.material ?? target.material,
    type: spec.type ?? target.type,
    density: spec.density ?? target.density,
    ...(spec.color ? { color: spec.color } : {})
  };
}

const cleanNumberList = list =>
  (Array.isArray(list) ? list : [])
    .map(num)
    .filter(v => v !== null && v > 0);

function serialiseWire(wire = {}) {
  const out = {
    material: upper(wire.material),
    type: upper(wire.type),
    density: num(wire.density),
    numberOfWires: cleanNumberList(wire.numberOfWires),
    diameter: num(wire.diameter),
    section: num(wire.section)
  };
  // Only stranded and multi-wire conductors carry the individual wire gauges;
  // a solid conductor has none, and writing an empty array where the existing
  // documents have no key at all would be a shape change.
  const gauges = cleanNumberList(wire.wireDiameter);
  if (gauges.length) out.wireDiameter = gauges;
  return out;
}

function serialiseIsolation(iso = {}, wireSection) {
  const section = num(iso.section);
  const filling =
    num(iso.fillingSection) ??
    (section !== null && num(wireSection) !== null ? round4(section - num(wireSection)) : null);

  return {
    material: upper(iso.material),
    type: upper(iso.type),
    density: num(iso.density),
    diameter: num(iso.diameter),
    section,
    fillingSection: filling,
    minRadial: num(iso.minRadial),
    maxRadial: num(iso.maxRadial),
    color: upper(iso.color)
  };
}

function serialisePhase(phase, chosenNames) {
  const dependsOn = resolveDependency(phase.name, chosenNames);

  switch (phase.name) {
    case 'pre_stranding': {
      const rope = (Array.isArray(phase.rope) ? phase.rope : []).filter(r => str(r?.material));

      // `rope[]` replaces the flat fields rather than sitting beside them. All
      // 16 standards stored this way carry `{ name, dependsOn, rope }` and
      // nothing else — the readers that handle both shapes prefer `rope[]` when
      // it is there, so a flat diameter alongside it is a second answer to the
      // same question, and the one nobody reads.
      if (rope.length) {
        return {
          name: 'pre_stranding',
          dependsOn,
          rope: rope.map(r => ({
            material: upper(r.material),
            type: upper(r.type),
            density: num(r.density),
            numberOfWires: cleanNumberList(r.numberOfWires),
            wireDiameter: cleanNumberList(r.wireDiameter),
            numberOfDrums: num(r.numberOfDrums),
            diameter: num(r.diameter)
          }))
        };
      }

      return {
        name: 'pre_stranding',
        dependsOn,
        material: upper(phase.material) || 'COPPER',
        type: upper(phase.type),
        density: num(phase.density),
        wireDiameter: cleanNumberList(phase.wireDiameter),
        numberOfWires: cleanNumberList(phase.numberOfWires),
        numberOfDrums: num(phase.numberOfDrums),
        diameter: num(phase.diameter),
        section: num(phase.section)
      };
    }

    case 'conductor_isolation': {
      const conductors = (Array.isArray(phase.conductors) ? phase.conductors : []).map(c => {
        const wire = serialiseWire(c?.wire);
        return { wire, isolation: serialiseIsolation(c?.isolation, wire.section) };
      });
      const out = { name: 'conductor_isolation', dependsOn, conductors };
      // Set on cables whose conductor is the finished product (FY, MYF): there
      // is nothing above it, and the launcher reads this to know the job ends here.
      if (phase.lastPhase) out.lastPhase = true;
      return out;
    }

    case 'rope':
      return {
        name: 'rope',
        dependsOn,
        diameter: num(phase.diameter),
        section: num(phase.section)
      };

    case 'interior_sheat':
    case 'exterior_sheat':
      return {
        name: phase.name,
        dependsOn,
        material: upper(phase.material),
        type: upper(phase.type),
        density: num(phase.density),
        diameter: num(phase.diameter),
        section: num(phase.section),
        fillingSection: num(phase.fillingSection),
        minRadial: num(phase.minRadial),
        maxRadial: num(phase.maxRadial)
      };

    case 'armouring':
      return {
        name: 'armouring',
        dependsOn,
        wide: num(phase.wide),
        thickness: num(phase.thickness),
        density: num(phase.density),
        diameter: num(phase.diameter),
        section: num(phase.section),
        fillingSection: num(phase.fillingSection)
      };

    default:
      return { ...phase, dependsOn };
  }
}

/**
 * The complete document to POST to /api/v1/standard.
 *
 * Phases come out in build order regardless of the order they were filled in,
 * because several consumers walk the array rather than sorting it.
 */
export function buildStandardPayload(header = {}, phases = []) {
  const ordered = [...phases]
    .filter(p => p?.name)
    .sort((a, b) => PHASE_ORDER.indexOf(a.name) - PHASE_ORDER.indexOf(b.name));
  const chosenNames = ordered.map(p => p.name);

  const mixed = Boolean(header.hasMixedConductorSections);

  return {
    name: upper(header.name),
    productStandard: str(header.productStandard),
    performanceStandard: str(header.performanceStandard),
    notifiedBody: str(header.notifiedBody),
    dop: str(header.dop),
    tension: str(header.tension),
    fireReaction: str(header.fireReaction),
    color: upper(header.color),
    numberOfConductors: num(header.numberOfConductors),
    section: num(header.section),
    hasMixedConductorSections: mixed,
    // Only meaningful when the cable really does mix sections; storing a number
    // otherwise would make the key that identifies a standard wrong.
    greenYellowSection: mixed ? num(header.greenYellowSection) : null,
    phases: ordered.map(p => serialisePhase(p, chosenNames))
  };
}

/**
 * What is still missing, in the order a person would fix it.
 *
 * Returned rather than thrown: the wizard shows them as it goes, and a standard
 * may legitimately be saved with gaps the engineer intends to fill later.
 */
export function validateStandard(header = {}, phases = []) {
  const problems = [];
  const add = (where, message) => problems.push({ where, message });

  if (!str(header.name)) add('header', 'Name is required');
  if (!(num(header.numberOfConductors) > 0)) add('header', 'Number of conductors is required');
  if (!(num(header.section) > 0)) add('header', 'Section is required');
  if (header.hasMixedConductorSections && !(num(header.greenYellowSection) > 0)) {
    add('header', 'A mixed-section cable needs its green-yellow section');
  }
  if (!phases.length) add('phases', 'At least one production phase is required');

  for (const phase of phases) {
    const label = PHASE_LABEL[phase.name] || phase.name;

    if (phase.name === 'conductor_isolation') {
      const conductors = Array.isArray(phase.conductors) ? phase.conductors : [];
      if (!conductors.length) add(phase.name, `${label}: at least one conductor is required`);
      conductors.forEach((c, i) => {
        if (!(num(c?.wire?.diameter) > 0)) add(phase.name, `${label}: conductor ${i + 1} has no wire diameter`);
        if (!str(c?.isolation?.material)) add(phase.name, `${label}: conductor ${i + 1} has no insulation material`);
      });
      continue;
    }

    if (phase.name === 'armouring') {
      if (!(num(phase.wide) > 0) || !(num(phase.thickness) > 0)) {
        add(phase.name, `${label}: tape width and thickness are required`);
      }
      continue;
    }

    if (phase.name === 'interior_sheat' || phase.name === 'exterior_sheat') {
      if (!str(phase.material)) add(phase.name, `${label}: material is required`);
      if (!(num(phase.diameter) > 0)) add(phase.name, `${label}: diameter is required`);
      continue;
    }

    if (phase.name === 'pre_stranding') {
      // Two shapes, two things to check. A cable drawn as several ropes has no
      // diameter of its own — each rope has one — and demanding the flat field
      // anyway is what stopped a mixed-section standard being saved at all.
      const ropes = Array.isArray(phase.rope) ? phase.rope.filter(r => str(r?.material)) : [];
      if (ropes.length) {
        ropes.forEach((r, i) => {
          if (!(num(r.diameter) > 0)) add(phase.name, `${label}: rope ${i + 1} has no diameter`);
          if (!cleanNumberList(r.wireDiameter).length) {
            add(phase.name, `${label}: rope ${i + 1} has no wire gauges`);
          }
        });
        if (header.hasMixedConductorSections && ropes.length < 2) {
          add(phase.name, `${label}: a mixed-section cable is two ropes, one per section`);
        }
      } else if (!(num(phase.diameter) > 0)) {
        add(phase.name, `${label}: diameter is required`);
      }
    }
  }

  return problems;
}

/** Section of a round conductor, the one derivation the form has always done. */
export const sectionFromDiameter = d => (num(d) > 0 ? round4(disc(d)) : null);
